/* =============================================================================
   NAJIBS — entrance sequence
   Stage 1  loading      thin progress line / percentage over the N pattern
   Stage 2  3D mark      the approved logo as a real <button>
   Stage 3  reveal       two panels part along the mark's own 38.2deg diagonal

   Progress reflects real loading. The minimum duration only stops the screen
   flashing past; it never waits on assets that have already arrived, and a hard
   ceiling guarantees the visitor always gets through.
   ============================================================================= */
(function () {
  'use strict';

  const root = document.getElementById('NajibsEntrance');
  if (!root || !document.documentElement.classList.contains('entrance-on')) return;

  /* Tell entrance-boot's failsafe that this file arrived and is about to take
     responsibility for the curtain. Set before any other work, so a later throw still
     leaves the ceiling timer below in charge rather than stranding the visitor. */
  window.__najibsEntranceReady = true;

  const STORAGE_KEY = 'najibs:entered';
  const mode = root.dataset.mode;
  const minDuration = Number(root.dataset.min || 900);
  const maxDuration = Math.max(Number(root.dataset.max || 2600), minDuration + 200);
  const wantsParallax = root.dataset.parallax === 'true';
  /* Theme settings -> Motion -> None means the CSS resolves every duration to 0, so the
     script must not sit waiting on transitions that will never run. */
  const motionOff = root.dataset.motion === 'none';
  const reduceMotion = motionOff || window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const button = root.querySelector('[data-entrance-enter]');
  const bar = root.querySelector('[data-entrance-bar]');
  const track = root.querySelector('[data-entrance-track]');
  const percent = root.querySelector('[data-entrance-percent]');
  const pattern = root.querySelector('.entrance__pattern');
  const start = performance.now();

  let displayed = 0;
  let entered = false;
  let revealed = false;
  let rafId = null;
  let pollId = null;
  let ceilingId = null;

  /* ------------------------------------------------------------ progress */

  /* Real signals, weighted. Each fires at most once. */
  let signal = 0;
  const bump = (value) => {
    signal = Math.max(signal, value);
  };

  if (document.readyState !== 'loading') bump(0.45);
  document.addEventListener('DOMContentLoaded', () => bump(0.55), { once: true });

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => bump(0.7));
  } else {
    bump(0.7);
  }

  if (document.readyState === 'complete') bump(1);
  window.addEventListener('load', () => bump(1), { once: true });

  /* Above-the-fold imagery counts too — the store should not be revealed
     onto a page of empty frames. */
  const heroImages = Array.from(document.querySelectorAll('img[fetchpriority="high"], img[loading="eager"]'))
    .filter((img) => !root.contains(img));

  if (heroImages.length) {
    let done = 0;
    const settle = () => {
      done += 1;
      bump(0.7 + 0.3 * (done / heroImages.length));
    };
    heroImages.forEach((img) => {
      if (img.complete) settle();
      else {
        img.addEventListener('load', settle, { once: true });
        img.addEventListener('error', settle, { once: true });
      }
    });
  }

  function paint(value) {
    const pct = Math.round(value * 100);
    if (bar) bar.style.transform = `scaleX(${value})`;
    if (percent) percent.textContent = String(pct);
    if (track) track.setAttribute('aria-valuenow', String(pct));
  }

  /* The decision to advance must never depend on requestAnimationFrame. A tab opened
     in the background has rAF throttled to nothing, and the visitor would come back to
     a frozen loader. rAF only paints; these two timers decide. */
  function shouldAdvance() {
    const elapsed = performance.now() - start;
    return (signal >= 1 && elapsed >= minDuration) || elapsed >= maxDuration;
  }

  function tick(now) {
    if (revealed) return;
    if (shouldAdvance()) {
      paint(1);
      return toStage2();
    }
    const elapsed = now - start;
    /* Time floor keeps the line moving even on an instant cache hit. */
    const byTime = Math.min(1, elapsed / maxDuration);
    const target = Math.max(signal * 0.94, byTime * 0.9);
    displayed += (Math.min(target, 0.985) - displayed) * 0.12;
    paint(displayed);
    rafId = requestAnimationFrame(tick);
  }

  /* ------------------------------------------------------- stage 2: mark */

  function toStage2() {
    if (revealed) return;
    revealed = true;

    if (rafId) cancelAnimationFrame(rafId);
    if (pollId) clearInterval(pollId);
    if (ceilingId) clearTimeout(ceilingId);
    paint(1);
    root.dataset.stage = 'enter';

    if (pattern) {
      pattern.style.opacity = pattern.dataset.targetOpacity || '0.06';
    }

    window.setTimeout(
      () => {
        if (button) button.focus({ preventScroll: true });
      },
      reduceMotion ? 0 : 320
    );

    if (wantsParallax && !reduceMotion && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      window.addEventListener('pointermove', onPointerMove, { passive: true });
    }
  }

  function onPointerMove(event) {
    const x = (event.clientX / window.innerWidth - 0.5) * 2;
    const y = (event.clientY / window.innerHeight - 0.5) * 2;
    root.style.setProperty('--px', x.toFixed(3));
    root.style.setProperty('--py', y.toFixed(3));
  }

  /* ----------------------------------------------------- stage 3: reveal */

  function enter() {
    if (entered) return;
    entered = true;

    try {
      window.sessionStorage.setItem(STORAGE_KEY, '1');
    } catch (e) {
      /* Storage blocked — the entrance will show again next navigation. */
    }

    window.removeEventListener('pointermove', onPointerMove);
    root.dataset.stage = 'leaving';
    root.setAttribute('aria-hidden', 'true');

    const finish = () => {
      document.documentElement.classList.remove('entrance-on');
      root.remove();
      const main = document.getElementById('MainContent');
      if (main) main.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('najibs:entered'));
      if (window.NAJIBS && typeof window.NAJIBS.initReveals === 'function') {
        window.NAJIBS.initReveals(document);
      }
    };

    if (reduceMotion) finish();
    else window.setTimeout(finish, 1180);
  }

  /* -------------------------------------------------------------- wiring */

  root.querySelectorAll('[data-entrance-enter]').forEach((el) => {
    el.addEventListener('click', (event) => {
      event.preventDefault();
      enter();
    });
    /* Space and Enter already fire click on a real <button>; this only adds the
       pressed-in look on key-down so keyboard use matches the pointer. */
    el.addEventListener('keydown', (event) => {
      if (event.key === ' ' || event.key === 'Enter') el.classList.add('is-pressed');
    });
    el.addEventListener('keyup', () => el.classList.remove('is-pressed'));
    el.addEventListener('blur', () => el.classList.remove('is-pressed'));
  });

  /* Escape is an accepted way out of any modal. */
  document.addEventListener('keydown', function onEsc(event) {
    if (event.key === 'Escape' && !entered) {
      enter();
      document.removeEventListener('keydown', onEsc);
    }
  });

  /* Keep focus inside the overlay while it is up. */
  document.addEventListener('focusin', (event) => {
    if (!entered && !root.contains(event.target) && button) {
      button.focus({ preventScroll: true });
    }
  });

  /* Anyone landing mid-session on a deep link should not be gated twice. */
  if (mode !== 'always') {
    window.addEventListener('pageshow', (event) => {
      if (event.persisted && entered) {
        document.documentElement.classList.remove('entrance-on');
      }
    });
  }

  if (reduceMotion) {
    toStage2();
  } else {
    rafId = requestAnimationFrame(tick);
    /* Timer-driven safety net: works while the tab is hidden and rAF is asleep. */
    pollId = window.setInterval(() => {
      if (shouldAdvance()) toStage2();
    }, 120);
    ceilingId = window.setTimeout(toStage2, maxDuration + 60);
    /* Coming back to a backgrounded tab should not replay a stale loader. */
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible' || revealed) return;
      if (shouldAdvance()) toStage2();
      else if (rafId == null) rafId = requestAnimationFrame(tick);
    });
  }
})();
